import type { ReactNode } from 'react';
import { Navigate } from '@tanstack/react-router';
import type { Role } from '@app/shared/schemas/auth';
import { useAuth } from './auth-provider';

/**
 * Client-side role guard — purely cosmetic; the real authorization is enforced
 * by the backend `authorize` plugin (ADR-0011). We use this to hide UI surface
 * the user wouldn't be able to use anyway and to short-circuit prefetches.
 *
 * Renders:
 *   - children, if the user is authenticated AND their role is in `allow`
 *   - the configured fallback (default: <Navigate to="/">), otherwise.
 *     Home rozcestník (#72025) je bezpečný cíl pro všechny role —
 *     filterNavForRole automaticky skryje dlaždice mimo uživatelovu roli.
 */
export interface RequireRoleProps {
  allow: Role[];
  children: ReactNode;
  fallback?: ReactNode;
}

export function RequireRole({ allow, children, fallback }: RequireRoleProps) {
  const auth = useAuth();

  if (auth.status === 'bootstrapping') {
    return (
      <div className="flex min-h-full items-center justify-center p-8 text-sm text-slate-500">
        Načítání…
      </div>
    );
  }

  if (auth.status !== 'authenticated' || !auth.user) {
    return <Navigate to="/login" replace />;
  }

  if (!allow.includes(auth.user.role)) {
    return fallback !== undefined ? <>{fallback}</> : <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
